/**
 * Communication Provider Settings Component
 * Allows organizations to choose between default and BYOP (Bring Your Own Provider)
 * for SMS, Email, Voice, and WhatsApp communications
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Phone,
  Mail,
  MessageSquare,
  Settings,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  QrCode,
  Users,
  Pencil,
  Send,
  X,
  Loader2
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import { ZoomProviderTools } from './ZoomProviderTools';
interface Provider {
  id: string;
  provider_type: string;
  channel: string;
  phone_number?: string;
  from_email?: string;
  is_active: boolean;
  priority: number;
  created_at: string;
  scope?: string;
  provisioned_by_platform?: boolean;
}

// Required credential fields per provider type — mirrors SUPPORTED_PROVIDERS
// in the backend (communication_provider_service.py). Drives the dynamic
// credential inputs in the add/edit modal.
const CREDENTIAL_FIELDS: Record<string, string[]> = {
  twilio: ['account_sid', 'auth_token'],
  vonage: ['api_key', 'api_secret'],
  plivo: ['auth_id', 'auth_token'],
  messagebird: ['access_key'],
  ringcentral: ['client_id', 'client_secret', 'jwt_token'],
  zoom_phone: ['account_id', 'client_id', 'client_secret'],
  sendgrid: ['api_key'],
  aws_ses: ['access_key_id', 'secret_access_key', 'region'],
  mailgun: ['api_key', 'domain'],
};

// Optional credentials, shown after the required ones and never enforced.
// Zoom's webhook_secret is the Zoom app's Secret Token (not its client secret):
// without it the provider's webhook URLs are checked against WeaveIn's own Zoom
// app, so Zoom's Validate fails for a customer's app.
const OPTIONAL_CREDENTIAL_FIELDS: Record<string, { field: string; placeholder: string }[]> = {
  zoom_phone: [{ field: 'webhook_secret', placeholder: 'webhook_secret (Zoom app Secret Token, optional)' }],
};

interface CommunicationSettings {
  provider_mode: 'default' | 'byop';
  phone_allocation_mode: 'shared' | 'per_user';
  messaging_service_sid: string;
  pinned_sender_number: string;
  qr_codes_enabled: boolean;
  qr_member_onboarding_enabled: boolean;
  qr_user_onboarding_enabled: boolean;
  user_phone_numbers_enabled: boolean;
}

interface CommunicationProviderSettingsProps {
  organizationId: number;
  initialSettings?: Partial<CommunicationSettings>;
  onSettingsChange?: (settings: CommunicationSettings) => void;
  isSetupMode?: boolean; // For org setup flow
  showAdvancedFeatures?: boolean; // Show QR codes and phone number toggles
}

const SUPPORTED_PROVIDERS = {
  sms: [
    { value: 'twilio', label: 'Twilio', description: 'Industry-leading SMS platform' },
    { value: 'vonage', label: 'Vonage', description: 'Global messaging platform' },
    { value: 'plivo', label: 'Plivo', description: 'Cloud communications' },
    { value: 'ringcentral', label: 'RingCentral', description: 'Business communications' },
    { value: 'zoom_phone', label: 'Zoom Phone', description: 'Unified communications' }
  ],
  email: [
    { value: 'sendgrid', label: 'SendGrid', description: 'Email delivery service' },
    { value: 'aws_ses', label: 'AWS SES', description: 'Amazon email service' },
    { value: 'mailgun', label: 'Mailgun', description: 'Email API service' }
  ],
  voice: [
    { value: 'twilio', label: 'Twilio', description: 'Programmable voice' },
    { value: 'vonage', label: 'Vonage', description: 'Voice API' },
    { value: 'plivo', label: 'Plivo', description: 'Voice platform' },
    { value: 'ringcentral', label: 'RingCentral', description: 'Business phone' },
    { value: 'zoom_phone', label: 'Zoom Phone', description: 'Cloud phone' }
  ],
  whatsapp: [
    { value: 'twilio', label: 'Twilio', description: 'WhatsApp Business API' },
    { value: 'messagebird', label: 'MessageBird', description: 'WhatsApp messaging' }
  ],
  rcs: [
    { value: 'twilio', label: 'Twilio', description: 'RCS Business Messaging (auto-falls back to SMS)' }
  ]
};

export function CommunicationProviderSettings({
  organizationId,
  initialSettings,
  onSettingsChange,
  isSetupMode = false,
  showAdvancedFeatures = true
}: CommunicationProviderSettingsProps) {
  const { t } = useAppTranslation();
  const [settings, setSettings] = useState<CommunicationSettings>({
    provider_mode: initialSettings?.provider_mode || 'default',
    phone_allocation_mode: initialSettings?.phone_allocation_mode || 'shared',
    messaging_service_sid: initialSettings?.messaging_service_sid || '',
    pinned_sender_number: initialSettings?.pinned_sender_number || '',
    qr_codes_enabled: initialSettings?.qr_codes_enabled || false,
    qr_member_onboarding_enabled: initialSettings?.qr_member_onboarding_enabled || false,
    qr_user_onboarding_enabled: initialSettings?.qr_user_onboarding_enabled || false,
    user_phone_numbers_enabled: initialSettings?.user_phone_numbers_enabled || false
  });
  const [resolvedNumber, setResolvedNumber] = useState<string | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add/Edit provider modal
  const emptyForm = {
    id: '' as string,
    channel: 'sms',
    provider_type: 'twilio',
    phone_number: '',
    priority: 1,
    scope: 'organization',
    credentials: {} as Record<string, string>,
  };
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerForm, setProviderForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Test-send modal
  const [testProvider, setTestProvider] = useState<Provider | null>(null);
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState('WeaveIn test message. Msg&data rates may apply. Reply STOP to opt out.');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  // Refs to prevent unnecessary operations
  const hasFetchedProviders = useRef(false);
  const lastOrgId = useRef(0); // Initialize to 0 so first load always syncs
  const hasInitialized = useRef(false);

  // Update settings when initialSettings prop changes (e.g., when org is loaded)
  useEffect(() => {
    // Sync on first mount or when organizationId changes
    const shouldSync = !hasInitialized.current || organizationId !== lastOrgId.current;

    if (initialSettings && shouldSync) {
      hasInitialized.current = true;
      lastOrgId.current = organizationId;
      setSettings({
        provider_mode: initialSettings.provider_mode || 'default',
        phone_allocation_mode: initialSettings.phone_allocation_mode || 'shared',
        messaging_service_sid: initialSettings.messaging_service_sid || '',
        pinned_sender_number: initialSettings.pinned_sender_number || '',
        qr_codes_enabled: initialSettings.qr_codes_enabled || false,
        qr_member_onboarding_enabled: initialSettings.qr_member_onboarding_enabled || false,
        qr_user_onboarding_enabled: initialSettings.qr_user_onboarding_enabled || false,
        user_phone_numbers_enabled: initialSettings.user_phone_numbers_enabled || false
      });
      hasFetchedProviders.current = false; // Reset fetch flag for new org
    }
  }, [organizationId, initialSettings]);

  // Hydrate phone-allocation fields from the backend so the per-user toggle
  // and BYOP/messaging-service inputs reflect what's actually persisted.
  // Without this, callers that pass initialSettings={undefined} (e.g. the
  // Org Settings page) would always show defaults regardless of saved state.
  useEffect(() => {
    if (organizationId <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(
          getWeaveinApiUrl(`/api/user-phone-numbers/org-config/${organizationId}`)
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          provider_mode: data.provider_mode || prev.provider_mode,
          phone_allocation_mode: data.phone_allocation_mode || prev.phone_allocation_mode,
          messaging_service_sid: data.messaging_service_sid || prev.messaging_service_sid,
          user_phone_numbers_enabled: !!data.user_phone_numbers_enabled,
        }));
      } catch (err) {
        console.error('Error hydrating phone config:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  // Fetch providers only when explicitly in BYOP mode and not in setup mode
  // This is separate from settings changes to avoid loops
  useEffect(() => {
    if (settings.provider_mode === 'byop' &&
        !isSetupMode &&
        organizationId > 0 &&
        !hasFetchedProviders.current) {
      hasFetchedProviders.current = true;
      fetchProviders();
    }
  }, [settings.provider_mode, organizationId, isSetupMode]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await authFetch(getWeaveinApiUrl(`/api/communication-providers/?organization_id=${organizationId}`));
      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddProvider = (channel: string = 'sms') => {
    const provider_type = SUPPORTED_PROVIDERS[channel as keyof typeof SUPPORTED_PROVIDERS]?.[0]?.value || 'twilio';
    setModalError(null);
    setIsEditing(false);
    setProviderForm({ ...emptyForm, channel, provider_type, credentials: {} });
    setProviderModalOpen(true);
  };

  const openEditProvider = (p: Provider) => {
    setModalError(null);
    setIsEditing(true);
    setProviderForm({
      id: p.id,
      channel: p.channel,
      provider_type: p.provider_type,
      phone_number: p.phone_number || '',
      priority: p.priority,
      scope: p.scope || 'organization',
      credentials: {}, // never pre-filled; leave blank to keep existing
    });
    setProviderModalOpen(true);
  };

  const handleSaveProvider = async () => {
    setModalError(null);
    const required = CREDENTIAL_FIELDS[providerForm.provider_type] || [];
    // On create, all credentials required; on edit they're optional (blank = keep).
    if (!isEditing) {
      const missing = required.filter(f => !(providerForm.credentials[f] || '').trim());
      if (missing.length) {
        setModalError(`Missing credential(s): ${missing.join(', ')}`);
        return;
      }
    }
    setSavingProvider(true);
    try {
      let response;
      if (isEditing) {
        const body: Record<string, any> = {
          phone_number: providerForm.phone_number || null,
          priority: providerForm.priority,
          scope: providerForm.scope,
        };
        // Only send credentials if the user re-entered any (rotation).
        const entered = Object.fromEntries(
          Object.entries(providerForm.credentials).filter(([, v]) => (v || '').trim())
        );
        if (Object.keys(entered).length) body.credentials = entered;
        response = await authFetch(getWeaveinApiUrl(`/api/communication-providers/${providerForm.id}`), {
          method: 'PUT',
          jsonBody: body,
        });
      } else {
        response = await authFetch(getWeaveinApiUrl('/api/communication-providers/'), {
          method: 'POST',
          jsonBody: {
            organization_id: organizationId,
            provider_type: providerForm.provider_type,
            channel: providerForm.channel,
            phone_number: providerForm.phone_number,
            credentials: providerForm.credentials,
            priority: providerForm.priority,
            scope: providerForm.scope,
          },
        });
      }
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save provider');
      }
      setProviderModalOpen(false);
      await fetchProviders();
    } catch (err: any) {
      setModalError(err.message || 'Failed to save provider');
    } finally {
      setSavingProvider(false);
    }
  };

  const handleDeleteProvider = async (p: Provider) => {
    if (!window.confirm(`Remove the ${p.provider_type} ${p.channel} provider${p.phone_number ? ` (${p.phone_number})` : ''}?`)) {
      return;
    }
    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/communication-providers/${p.id}/deactivate`), {
        method: 'POST',
      });
      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to remove provider');
      }
      await fetchProviders();
    } catch (err: any) {
      setError(err.message || 'Failed to remove provider');
    }
  };

  const openTestProvider = (p: Provider) => {
    setTestProvider(p);
    setTestResult(null);
    setTestNumber('');
  };

  const handleSendTest = async () => {
    if (!testProvider) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/communication-providers/${testProvider.id}/test`), {
        method: 'POST',
        jsonBody: {
          provider_id: testProvider.id,
          test_number: testNumber,
          test_message: testMessage,
        },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && (data.status === 'sent' || data.status === 'success')) {
        setTestResult({ ok: true, text: `Sent via ${data.provider}. Message id: ${data.message_sid || 'n/a'}` });
      } else {
        setTestResult({ ok: false, text: data.detail || data.error || `Failed (status: ${data.status || response.status})` });
      }
    } catch (err: any) {
      setTestResult({ ok: false, text: err.message || 'Network error' });
    } finally {
      setSendingTest(false);
    }
  };

  const savePhoneConfig = async () => {
    try {
      setSavingConfig(true);
      setError(null);
      const response = await authFetch(getWeaveinApiUrl('/api/user-phone-numbers/org-config'), {
        method: 'POST',
        jsonBody: {
            organization_id: organizationId,
            phone_allocation_mode: settings.phone_allocation_mode,
            user_phone_numbers_enabled: settings.user_phone_numbers_enabled,
            messaging_service_sid: settings.messaging_service_sid || null,
            provider_mode: settings.provider_mode
          }
      });
      if (!response.ok) {
        throw new Error('Failed to save phone configuration');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const resolvePhoneNumber = async () => {
    try {
      const response = await authFetch(getWeaveinApiUrl(`/api/user-phone-numbers/resolve/${organizationId}?channel=sms`));
      if (response.ok) {
        const data = await response.json();
        setResolvedNumber(data.from_number);
      }
    } catch (err) {
      console.error('Error resolving phone number:', err);
    }
  };

  const handleSettingChange = (key: keyof CommunicationSettings, value: any) => {
    const newSettings = {
      ...settings,
      [key]: value
    };

    // Phone allocation mode is the single source of truth for whether
    // per-user numbers are in play — keep user_phone_numbers_enabled in
    // lockstep so the resolver (which checks both columns) can't see them
    // contradict each other.
    if (key === 'phone_allocation_mode') {
      newSettings.user_phone_numbers_enabled = value === 'per_user';
    }

    setSettings(newSettings);

    // Notify parent immediately when user makes a change
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'sms':
        return <MessageSquare className="h-5 w-5" />;
      case 'email':
        return <Mail className="h-5 w-5" />;
      case 'voice':
        return <Phone className="h-5 w-5" />;
      case 'whatsapp':
        return <MessageSquare className="h-5 w-5 text-green-600" />;
      default:
        return <Settings className="h-5 w-5" />;
    }
  };

  const getProvidersByChannel = (channel: string) => {
    // Show this org's active, org-level providers: platform-assigned numbers
    // (scope 'organization') and any BYOP providers (incl. scope 'test').
    // Exclude per-user personal numbers (scope 'user' — managed elsewhere) and
    // deactivated rows.
    return providers.filter(
      p =>
        p.channel === channel &&
        p.is_active !== false &&
        p.scope !== 'user'
    );
  };

  return (
    <div className="space-y-6">
      {/* Provider Mode Selection */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('settings.communication_provider_mode', 'Communication Provider Mode')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('settings.choose_how_your_organization_handles', 'Choose how your organization handles SMS, Email, and Voice communications.')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Default Mode */}
          <div
            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
              settings.provider_mode === 'default'
                ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            onClick={() => handleSettingChange('provider_mode', 'default')}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${
                settings.provider_mode === 'default' ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-700'
              }`}>
                <Settings className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-white">{t('settings.platform_default', 'Platform Default')}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('settings.use_the_platforms_builtin_communication', 'Use the platform\'s built-in communication providers. No setup required.')}
                </p>
                <ul className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-1">
                  <li>• Instant setup</li>
                  <li>• No API credentials needed</li>
                  <li>• Usage-based billing</li>
                </ul>
              </div>
            </div>
            {settings.provider_mode === 'default' && (
              <CheckCircle className="absolute top-3 right-3 h-5 w-5 text-blue-600 dark:text-blue-400" />
            )}
          </div>

          {/* BYOP Mode */}
          <div
            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
              settings.provider_mode === 'byop'
                ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
            onClick={() => handleSettingChange('provider_mode', 'byop')}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${
                settings.provider_mode === 'byop' ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-700'
              }`}>
                <Phone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-white">Bring Your Own Provider (BYOP)</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('settings.use_your_own_twilio_sendgrid', 'Use your own Twilio, SendGrid, or other provider accounts.')}
                </p>
                <ul className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-1">
                  <li>• Full control over accounts</li>
                  <li>• Your own phone numbers</li>
                  <li>• Direct billing with provider</li>
                </ul>
              </div>
            </div>
            {settings.provider_mode === 'byop' && (
              <CheckCircle className="absolute top-3 right-3 h-5 w-5 text-blue-600 dark:text-blue-400" />
            )}
          </div>
        </div>
      </div>

      {/* BYOP Provider Configuration */}
      {settings.provider_mode === 'byop' && !isSetupMode && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.provider_configuration', 'Provider Configuration')}</h3>
            <button
              onClick={() => openAddProvider(expandedChannel || 'sms')}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              {t('settings.add_provider', 'Add Provider')}
            </button>
          </div>

          <div className="space-y-4">
            {['sms', 'email', 'voice', 'whatsapp', 'rcs'].map(channel => (
              <div key={channel} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={() => setExpandedChannel(expandedChannel === channel ? null : channel)}
                >
                  <div className="flex items-center gap-3">
                    {getChannelIcon(channel)}
                    <div>
                      <h4 className="font-medium capitalize text-gray-900 dark:text-white">{channel}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {getProvidersByChannel(channel).length} provider(s) configured
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getProvidersByChannel(channel).length > 0 && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                        {t('settings.active', 'Active')}
                      </span>
                    )}
                    {expandedChannel === channel ? (
                      <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    )}
                  </div>
                </div>

                {expandedChannel === channel && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                    {getProvidersByChannel(channel).length > 0 ? (
                      <div className="space-y-2">
                        {getProvidersByChannel(channel).map(provider => (
                          <div
                            key={provider.id}
                            className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700"
                          >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium capitalize text-gray-900 dark:text-white">
                                {provider.provider_type}
                              </span>
                              {provider.phone_number && (
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  {provider.phone_number}
                                </span>
                              )}
                              {provider.from_email && (
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  {provider.from_email}
                                </span>
                              )}
                              {provider.provisioned_by_platform ? (
                                <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                                  Platform-assigned
                                </span>
                              ) : (
                                <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                                  BYOP
                                </span>
                              )}
                              {provider.scope === 'test' && (
                                <span className="ml-2 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
                                  Test only (not used for live sends)
                                </span>
                              )}
                              {provider.scope === 'bulk' && (
                                <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded">
                                  {t('settings.provider_scope_bulk_badge', 'Group & bulk sends only')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Priority: {provider.priority}
                              </span>
                              {(provider.channel === 'sms' || provider.channel === 'whatsapp' || provider.channel === 'voice') && (
                                <button
                                  onClick={() => openTestProvider(provider)}
                                  title="Send a test message"
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 text-xs"
                                >
                                  <Send className="h-3.5 w-3.5" /> Test
                                </button>
                              )}
                              {/* Platform-assigned numbers are managed by provisioning,
                                  not editable/removable from the BYOP screen. */}
                              {!provider.provisioned_by_platform && (
                                <>
                                  <button
                                    onClick={() => openEditProvider(provider)}
                                    title="Edit provider"
                                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProvider(provider)}
                                    title="Remove provider"
                                    className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {provider.provider_type === 'zoom_phone' && <ZoomProviderTools provider={provider} />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          No {channel} provider configured
                        </p>
                        <button
                          onClick={() => openAddProvider(channel)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          + Add {channel.toUpperCase()} Provider
                        </button>
                      </div>
                    )}

                    {/* Supported Providers Info */}
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settings.supported_providers', 'Supported providers:')}</p>
                      <div className="flex flex-wrap gap-2">
                        {SUPPORTED_PROVIDERS[channel as keyof typeof SUPPORTED_PROVIDERS]?.map(p => (
                          <span
                            key={p.value}
                            className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded"
                          >
                            {p.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phone Number & A2P Configuration */}
      {!isSetupMode && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Phone Number & A2P Configuration</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Configure the sender number and A2P 10DLC messaging service for SMS compliance and deliverability.
          </p>

          {/* Phone Allocation Mode */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Allocation Mode</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div
                className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                  settings.phone_allocation_mode === 'shared'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => handleSettingChange('phone_allocation_mode', 'shared')}
              >
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-sm text-gray-900 dark:text-white">Shared Number</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  One phone number for all organization outreach
                </p>
              </div>
              <div
                className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                  settings.phone_allocation_mode === 'per_user'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => handleSettingChange('phone_allocation_mode', 'per_user')}
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-sm text-gray-900 dark:text-white">Per-User Numbers</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Each user/agent gets a dedicated phone number
                </p>
              </div>
            </div>
          </div>

          {/* Pinned Sender Number */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Pinned Sender Number
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              The phone number all outbound SMS/voice will send from (E.164 format, e.g. +16507501631)
            </p>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="+16507501631"
              value={settings.pinned_sender_number}
              onChange={(e) => handleSettingChange('pinned_sender_number', e.target.value)}
            />
          </div>

          {/* Messaging Service SID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Twilio Messaging Service SID (A2P 10DLC)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Routes SMS through your registered A2P 10DLC campaign for compliance and better deliverability
            </p>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={settings.messaging_service_sid}
              onChange={(e) => handleSettingChange('messaging_service_sid', e.target.value)}
            />
          </div>

          {/* Resolve Preview */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={resolvePhoneNumber}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
            >
              Preview resolved sender number
            </button>
            {resolvedNumber && (
              <span className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded">
                Will send from: {resolvedNumber}
              </span>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={savePhoneConfig}
            disabled={savingConfig}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {savingConfig ? 'Saving...' : 'Save Phone Configuration'}
          </button>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{error}</p>
          )}
        </div>
      )}

      {/* Additional Features - only shown when showAdvancedFeatures is true */}
      {showAdvancedFeatures && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('settings.additional_features', 'Additional Features')}</h3>

          {/* QR Codes */}
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <QrCode className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{t('settings.qr_code_onboarding', 'QR Code Onboarding')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('settings.enable_qr_codes_for_member', 'Enable QR codes for member and user onboarding')}
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={settings.qr_codes_enabled}
                  onChange={(e) => handleSettingChange('qr_codes_enabled', e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {settings.qr_codes_enabled && (
              <div className="ml-8 space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                    checked={settings.qr_member_onboarding_enabled}
                    onChange={(e) => handleSettingChange('qr_member_onboarding_enabled', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.enable_qr_codes_for_member_1', 'Enable QR codes for member onboarding')}</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                    checked={settings.qr_user_onboarding_enabled}
                    onChange={(e) => handleSettingChange('qr_user_onboarding_enabled', e.target.checked)}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.enable_qr_codes_for_user', 'Enable QR codes for user invitations')}</span>
                </label>
              </div>
            )}

            {/* User-specific phone numbers is now controlled by the
                Phone Allocation Mode picker above; the standalone toggle
                was redundant and could contradict the mode picker. */}
          </div>
        </div>
      )}

      {/* Info Banner for Setup Mode */}
      {isSetupMode && settings.provider_mode === 'byop' && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-200">{t('settings.provider_setup_required', 'Provider Setup Required')}</h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                After creating your organization, you'll need to configure your communication
                providers in the settings. Have your Twilio, SendGrid, or other provider
                credentials ready.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Provider modal */}
      {providerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !savingProvider && setProviderModalOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isEditing ? 'Edit Provider' : 'Add Provider'}
              </h3>
              <button onClick={() => setProviderModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Channel</label>
                  <select
                    disabled={isEditing}
                    value={providerForm.channel}
                    onChange={e => {
                      const channel = e.target.value;
                      const pt = SUPPORTED_PROVIDERS[channel as keyof typeof SUPPORTED_PROVIDERS]?.[0]?.value || 'twilio';
                      setProviderForm({ ...providerForm, channel, provider_type: pt, credentials: {} });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
                  >
                    {['sms', 'email', 'voice', 'whatsapp', 'rcs'].map(c => (
                      <option key={c} value={c}>{c.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Provider</label>
                  <select
                    disabled={isEditing}
                    value={providerForm.provider_type}
                    onChange={e => setProviderForm({ ...providerForm, provider_type: e.target.value, credentials: {} })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
                  >
                    {(SUPPORTED_PROVIDERS[providerForm.channel as keyof typeof SUPPORTED_PROVIDERS] || []).map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {providerForm.channel !== 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sender phone number</label>
                  <input
                    type="text"
                    value={providerForm.phone_number}
                    onChange={e => setProviderForm({ ...providerForm, phone_number: e.target.value })}
                    placeholder="+14085551234"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Credentials {isEditing && <span className="text-xs font-normal text-gray-400">(leave blank to keep existing)</span>}
                </label>
                <div className="space-y-2">
                  {(CREDENTIAL_FIELDS[providerForm.provider_type] || []).map(field => (
                    <input
                      key={field}
                      type="password"
                      autoComplete="new-password"
                      value={providerForm.credentials[field] || ''}
                      onChange={e => setProviderForm({ ...providerForm, credentials: { ...providerForm.credentials, [field]: e.target.value } })}
                      placeholder={field}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ))}
                  {(OPTIONAL_CREDENTIAL_FIELDS[providerForm.provider_type] || []).map(({ field, placeholder }) => (
                    <input
                      key={field}
                      type="password"
                      autoComplete="new-password"
                      value={providerForm.credentials[field] || ''}
                      onChange={e => setProviderForm({ ...providerForm, credentials: { ...providerForm.credentials, [field]: e.target.value } })}
                      placeholder={placeholder}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1">Stored encrypted at rest.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                  <input
                    type="number"
                    min={1}
                    value={providerForm.priority}
                    onChange={e => setProviderForm({ ...providerForm, priority: parseInt(e.target.value, 10) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-400 mt-1">Lower = preferred.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Scope</label>
                  <select
                    value={providerForm.scope}
                    onChange={e => setProviderForm({ ...providerForm, scope: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="organization">Live (organization)</option>
                    <option value="bulk">{t('settings.provider_scope_bulk_option', 'Group & bulk sends only')}</option>
                    <option value="test">Test only</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Test = never used for live sends; only reachable via the Test button.</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t(
                      'settings.provider_scope_bulk_help',
                      'Group & bulk = used for campaigns and group messages instead of the live provider; one-to-one texts, calls and login codes keep using the live provider.'
                    )}
                  </p>
                </div>
              </div>

              {modalError && <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setProviderModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProvider}
                disabled={savingProvider}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {savingProvider && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEditing ? 'Save changes' : 'Add provider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test-send modal */}
      {testProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !sendingTest && setTestProvider(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Send test via {testProvider.provider_type}
              </h3>
              <button onClick={() => setTestProvider(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {testProvider.phone_number && (
                <p className="text-xs text-gray-500 dark:text-gray-400">From: {testProvider.phone_number}</p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Recipient number (E.164)</label>
                <input
                  type="text"
                  value={testNumber}
                  onChange={e => setTestNumber(e.target.value)}
                  placeholder="+14085551234"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                <textarea
                  rows={3}
                  value={testMessage}
                  onChange={e => setTestMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              {testResult && (
                <p className={`text-sm ${testResult.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {testResult.text}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setTestProvider(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100">
                Close
              </button>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testNumber.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommunicationProviderSettings;
